/**
 * EvaluatorsGuide: the visual guide of the experts page (/evaluators), opened by the "?" after the page's title
 * (HelpTitle helpId="evaluators"). The page lists everyone who voted on the questions the reader reads (For /
 * Against: the votes of the question page's vote form outside folders, and the subjects' likes and dislikes of its
 * vote bar) with their totals; an expert opens in a window (Evaluators/Modal.js) with their evaluations, a vote
 * filter, a search and a status filter.
 * It follows the page's breakpoints: the table turns into cards under 768px (Evaluators.js), and the filters move
 * under the "Filters" toggle under 640px (FilterPanel, on the page and in the window). Only approve_question opens
 * the page (utils/permissions.js), so its readers are admins and approvers; the roles block says who sees what, and
 * who is listed without opening it (the subjects' experts and moderators, the voters).
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  AdjustmentsHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClockIcon,
  // The voter's role pill, outlined like the other roles' (the solid thumbs are the votes').
  HandThumbUpIcon as VoterIcon,
  KeyIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/20/solid";
import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniChip,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniTable,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";

// A vote count as the experts list shows it: the thumb in the vote's colour, the number plain; a zero is grey.
function VoteCount({ up = false, value }) {
  return (
    <MiniRow className="flex-nowrap">
      <MiniIcon icon={up ? HandThumbUpIcon : HandThumbDownIcon} tone={value ? (up ? "green" : "red") : "slate"} />
      <MiniText variant={value ? "body" : "muted"}>{value}</MiniText>
    </MiniRow>
  );
}

// One number of the totals card: its label above it (truncate: one line each, under each other).
function Stat({ label, value }) {
  return (
    <MiniStack tight>
      <MiniText variant="muted" truncate>
        {label}
      </MiniText>
      <MiniText variant="title" truncate>
        {value}
      </MiniText>
    </MiniStack>
  );
}

export default function EvaluatorsGuide() {
  const [t] = useTranslation();
  // The page's own breakpoints: the table or the cards at md, FilterPanel (the page's and the window's) at sm.
  const wide = useMediaQuery("(min-width: 768px)");
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.evaluators.example.${key}`);

  const up = t("evaluators.upvoted");
  const down = t("evaluators.downvoted");
  // The subjects' vote bar: its likes and dislikes count here as the list's For and Against.
  const like = t("question_detail.vote_bar.like");
  const dislike = t("question_detail.vote_bar.dislike");
  const filters = t("question_list.filters_short");
  const totals = [
    { key: "total", label: t("evaluators.stats.total_evaluators"), value: 12 },
    { key: "active", label: t("evaluators.stats.active_this_month"), value: 5 },
    { key: "reviews", label: t("evaluators.stats.total_reviews"), value: 148 },
    { key: "month", label: t("evaluators.stats.reviews_this_month"), value: 23 },
  ];
  const experts = [
    { key: "e1", name: own("expert"), reviews: 24, up: 20, down: 4 },
    { key: "e2", name: own("expert_2"), reviews: 9, up: 6, down: 3 },
  ];

  return (
    <>
      <HelpIntro>
        <HelpText
          i18nKey="help.evaluators.intro"
          values={{ like, dislike, up: t("evaluators.table.upvotes"), down: t("evaluators.table.downvotes") }}
        />
      </HelpIntro>

      <HelpFlow pipeline="bts" current="review" />

      <HelpSteps>
        <HelpStep
          title={t("help.evaluators.steps.totals.title")}
          illustration={
            <MiniScreen title={t("nav.evaluators")}>
              {/* The card is marked as a whole (nothing on it is to click): its padding keeps the marker off the
                  texts. */}
              {roomy ? (
                <MiniCard mark={{ pointer: false }}>
                  <MiniRow className="justify-between gap-y-3">
                    <Stat label={totals[0].label} value={totals[0].value} />
                    <Stat label={totals[1].label} value={totals[1].value} />
                  </MiniRow>
                  <MiniDivider />
                  <MiniRow className="justify-between gap-y-3">
                    <Stat label={totals[2].label} value={totals[2].value} />
                    <Stat label={totals[3].label} value={totals[3].value} />
                  </MiniRow>
                </MiniCard>
              ) : (
                // Phones list the totals, each number at the end of its line.
                <MiniCard mark={{ pointer: false }}>
                  {totals.map(({ key, label, value }, index) => (
                    <React.Fragment key={key}>
                      {index > 0 && <MiniDivider />}
                      <MiniRow className="justify-between flex-nowrap">
                        <MiniText variant="muted">{label}</MiniText>
                        <MiniText variant="strong">{value}</MiniText>
                      </MiniRow>
                    </React.Fragment>
                  ))}
                </MiniCard>
              )}
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.evaluators.steps.totals.text" values={{ active: t("evaluators.stats.active_this_month") }} />
        </HelpStep>

        <HelpStep
          title={t("help.evaluators.steps.find.title")}
          illustration={
            <MiniScreen title={t("nav.evaluators")}>
              <MiniCard>
                {roomy ? (
                  <MiniRow className="gap-y-3">
                    <MiniField icon={MagnifyingGlassIcon} placeholder={t("evaluators.search_placeholder")} className="flex-1" />
                    <MiniField select value={t("evaluators.filters.all_subjects")} mark />
                  </MiniRow>
                ) : (
                  <MiniRow className="flex-nowrap">
                    <MiniField icon={MagnifyingGlassIcon} placeholder={t("evaluators.search_placeholder")} className="flex-1" />
                    <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} chevron mark>
                      {filters}
                    </MiniButton>
                  </MiniRow>
                )}
              </MiniCard>
              <MiniRow className="justify-end">
                {roomy && <MiniText variant="muted">{t("question_list.sort")}</MiniText>}
                <MiniField select value={t("evaluators.sort_options.reviews_desc")} />
              </MiniRow>
            </MiniScreen>
          }
        >
          {roomy ? (
            <HelpText
              i18nKey="help.evaluators.steps.find.text"
              values={{ subjects: t("evaluators.filters.all_subjects"), sort: t("question_list.sort") }}
            />
          ) : (
            <HelpText
              i18nKey="help.evaluators.steps.find.text_phone"
              values={{ filters, order: t("evaluators.sort_options.reviews_desc") }}
            />
          )}
        </HelpStep>

        <HelpStep
          title={t("help.evaluators.steps.open.title")}
          illustration={
            wide ? (
              <MiniTable
                columns={[
                  { label: t("evaluators.table.evaluator"), width: "40%" },
                  { label: t("evaluators.table.reviews"), align: "center" },
                  { label: t("evaluators.table.upvotes"), align: "center" },
                  { label: t("evaluators.table.downvotes"), align: "center" },
                ]}
                rows={experts.map((e) => [
                  <MiniText variant="strong" truncate>
                    {e.name}
                  </MiniText>,
                  String(e.reviews),
                  <VoteCount up value={e.up} />,
                  <VoteCount value={e.down} />,
                ])}
                markRow={0}
              />
            ) : (
              // A tap anywhere on a card opens the expert: the first card is marked as a whole.
              <MiniStack>
                {experts.map((e, index) => (
                  <MiniCard key={e.key} mark={index === 0}>
                    <MiniText variant="strong">{e.name}</MiniText>
                    <MiniRow className="gap-y-3">
                      <MiniText>{t("evaluators.list.reviews", { count: e.reviews })}</MiniText>
                      <VoteCount up value={e.up} />
                      <VoteCount value={e.down} />
                    </MiniRow>
                  </MiniCard>
                ))}
              </MiniStack>
            )
          }
        >
          <HelpText
            i18nKey="help.evaluators.steps.open.text"
            values={{ up: t("evaluators.table.upvotes"), down: t("evaluators.table.downvotes") }}
          />
        </HelpStep>

        <HelpStep
          title={t("help.evaluators.steps.filter.title")}
          illustration={
            <MiniScreen title={own("expert")}>
              <MiniRow className="gap-y-3">
                <MiniChip selected>{t("question_list.status_all")}</MiniChip>
                <MiniChip dot="green">{up}</MiniChip>
                <MiniChip dot="red" mark>
                  {down}
                </MiniChip>
              </MiniRow>
              <MiniCard>
                {roomy ? (
                  <MiniRow>
                    <MiniField icon={MagnifyingGlassIcon} placeholder={t("evaluators.my_evaluations.search_placeholder")} className="flex-1" />
                    <MiniField select placeholder={t("exam_taking.all_statuses")} />
                  </MiniRow>
                ) : (
                  <MiniRow className="flex-nowrap">
                    <MiniField
                      icon={MagnifyingGlassIcon}
                      placeholder={t("evaluators.my_evaluations.search_placeholder_short")}
                      className="flex-1"
                    />
                    <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} chevron>
                      {filters}
                    </MiniButton>
                  </MiniRow>
                )}
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.evaluators.steps.filter.text" values={{ up, down }} />
          <p>
            {roomy ? (
              <HelpText i18nKey="help.evaluators.steps.filter.search" values={{ statuses: t("exam_taking.all_statuses") }} />
            ) : (
              <HelpText i18nKey="help.evaluators.steps.filter.search_phone" values={{ filters }} />
            )}
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.evaluators.steps.read.title")}
          illustration={
            // A click anywhere on the row opens the question (the name is its link): the row is marked as a whole,
            // so the marker leaves the name's new-tab icon in sight.
            <MiniScreen title={own("expert")}>
              <MiniCard mark>
                <MiniRow className="gap-y-3">
                  <MiniRow className="flex-nowrap">
                    <MiniText variant="strong">{ex("question_1")}</MiniText>
                    <MiniIcon icon={ArrowTopRightOnSquareIcon} />
                  </MiniRow>
                  <MiniStatus code="APPROVED" />
                </MiniRow>
                <MiniRow>
                  <MiniBadge tone="green" icon={HandThumbUpIcon}>
                    {up}
                  </MiniBadge>
                  <MiniIcon icon={ClockIcon} />
                  <MiniText variant="muted">{own("date")}</MiniText>
                </MiniRow>
                <MiniText>{own("comment")}</MiniText>
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.evaluators.steps.read.text" />
        </HelpStep>
      </HelpSteps>

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.evaluators.roles.admin") },
          {
            key: "approver",
            role: t("question_detail.roles.approver"),
            Icon: CheckCircleIcon,
            can: <HelpText i18nKey="help.evaluators.roles.approver" values={{ none: t("folders.no_folder") }} />,
          },
          {
            key: "expert",
            role: t("folders.roles.expert"),
            Icon: CheckBadgeIcon,
            can: <HelpText i18nKey="help.evaluators.roles.expert" values={{ like, dislike, mine: t("nav.my_evaluations") }} />,
          },
          {
            key: "voter",
            role: t("help.my_evaluations.roles.voter_label"),
            Icon: VoterIcon,
            can: <HelpText i18nKey="help.evaluators.roles.voter" values={{ mine: t("nav.my_evaluations") }} />,
          },
        ]}
      />

      <HelpTips
        items={[
          <HelpText key="month" i18nKey="help.evaluators.tips.month" values={{ month: t("evaluators.stats.reviews_this_month") }} />,
          t("help.evaluators.tips.one_vote"),
          <HelpText key="decisions" i18nKey="help.evaluators.tips.decisions" values={{ history: t("question_detail.history.title") }} />,
        ]}
      />
    </>
  );
}
